import * as React from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as Device from 'react-device-detect';
import moment from 'moment';

import {
  useListHomeEventsQuery,
  ListHomeSpotsQuery,
  useListHomeSpotsQuery,
  ListHomeEventsQuery,
} from '@graphql';
import { truncateText } from '@utils';
import useStyles from './styles';
import { Insight, Extra } from './Incentives';
import { Item } from './types';
import { TFunction } from 'i18next';

const TRANSITION_DURATION = 800;

interface HighlightProps {
  type: 'spot' | 'event' | 'maker';
  setHighlightReady?: (ready: boolean) => void;
}

export function Highlight(props: HighlightProps) {
  const { type, setHighlightReady } = props;
  const [item, setItem] = React.useState<Item | null>(null);
  const [loaded, setLoaded] = React.useState(false);
  const [hideSkeleton, setHideSkeleton] = React.useState(false);
  const { isPortrait } = Device.useMobileOrientation();
  const { pathname } = useLocation();
  const theme = useTheme();
  const styles = useStyles(theme, item?.cover, item?.isGray);
  const navigate = useNavigate();
  const { t } = useTranslation('spot');

  const { data: eventData } = useListHomeEventsQuery({
    skip: type !== 'event',
    fetchPolicy: 'cache-first',
  });

  const { data: spotData } = useListHomeSpotsQuery({
    fetchPolicy: 'cache-first',
    skip: type !== 'spot',
  });

  React.useEffect(() => {
    if (type === 'spot' && spotData?.homeSpots) {
      const item = getRandomSpotItem(spotData.homeSpots, t);
      setItem(item);
    } else if (type === 'event' && eventData?.eventCategories) {
      const item = getRandomEventItem(eventData.eventCategories);
      setItem(item);
    }
  }, [spotData, eventData]);

  React.useEffect(() => {
    if (loaded) {
      const timer = setTimeout(() => {
        setHideSkeleton(true);
        setHighlightReady && setHighlightReady(true);
      }, TRANSITION_DURATION); // wait TRANSITION_DURATION before hiding the skeleton

      return () => clearTimeout(timer);
    }
  }, [loaded]);

  const handleOpen = () => {
    const path = type === 'spot' ? `s=${item?.slug}` : `e=${item?.id}`;
    navigate(`${pathname}?${path}`, { preventScrollReset: true });
  };

  if (!item || (Device.isMobileOnly && !isPortrait)) {
    return null;
  }

  if (Device.isMobileOnly) {
    return (
      <Stack
        position="relative"
        justifyContent="center"
        alignItems="center"
        width="100%"
        height="60vh"
        minHeight="60vh"
        px={2}
        onClick={handleOpen}
      >
        <Stack width="100%" height="100%" sx={{ position: 'absolute' }} p={2}>
          <Skeleton
            variant="rounded"
            width="100%"
            height="100%"
            sx={{
              inset: 0,
              opacity: item ? 0 : 1,
              pointerEvents: item ? 'none' : 'auto',
              transition: 'opacity 400ms ease-out',
            }}
          />
        </Stack>

        {/* CONTENU */}
        <Stack
          width="100%"
          height="100%"
          sx={{
            opacity: item ? 1 : 0,
            transform: item ? 'translateY(0)' : 'translateY(8px)',
            transition: 'opacity 500ms ease-out, transform 300ms ease-out',
          }}
        >
          <Stack
            width="100%"
            height="100%"
            sx={{
              backgroundColor: alpha(theme.palette.background.paper, 0.8),
              borderRadius: 2,
              boxShadow: `0px 0px 30px ${alpha('#000', 1)}`,
              border: `1px solid  ${alpha('#fff', 0.1)}`,
              borderStyle: 'inset',
              backgroundImage: `url('${item.cover}')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            <Stack
              spacing={2}
              p={2}
              width="100%"
              height="100%"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                backdropFilter: 'blur(15px)',
                backgroundColor: alpha(theme.palette.background.paper, 0.7),
                borderRadius: 2,
                boxShadow: `0px 0px 30px ${alpha('#000', 1)}`,
                border: `1px solid  ${alpha('#fff', 0.1)}`,
                borderStyle: 'inset',
              }}
            >
              <Stack
                width="100%"
                height="100%"
                sx={{
                  position: 'relative',
                }}
              >
                <Skeleton
                  variant="rectangular"
                  sx={{
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    inset: 0,
                    borderRadius: 3,
                    opacity: loaded ? 0 : 1,
                    transition: `opacity ${TRANSITION_DURATION}ms ease-out`,
                    display: hideSkeleton ? 'none' : 'block',
                  }}
                />
                <Box
                  className="img"
                  component="img"
                  src={item.cover}
                  alt="highlight cover"
                  width="100%"
                  height="inherit"
                  onLoad={() => {
                    setLoaded(true);
                  }}
                  sx={{
                    aspectRatio: '6/3',
                    objectFit: 'cover',
                    objectPosition: 'center',
                    borderRadius: 3,
                    border: `1px solid  ${alpha('#fff', 0.1)}`,
                    borderStyle: 'inset',
                    boxShadow: `0px 0px 30px ${alpha('#000', 0.5)}`,
                    // filter: item.isGray ? 'grayscale(60%)' : 'none',
                    opacity: loaded ? 1 : 0,
                    transition: `opacity ${TRANSITION_DURATION}ms ease-out`,
                  }}
                />
              </Stack>

              {/* text + button */}
              <Stack spacing={1} width="100%" alignItems="center">
                <Stack width="100%">
                  <Typography
                    textAlign="center"
                    variant="h6"
                    fontWeight={800}
                    sx={{ textShadow: `1px 1px 5px ${alpha('#000', 0.8)}` }}
                  >
                    {item.title}
                  </Typography>
                  <Typography textAlign="center" variant="body2" fontWeight={600}>
                    {item.insight}
                  </Typography>
                  <Typography textAlign="center" variant="body2">
                    {item.city}
                  </Typography>
                </Stack>
                <Button
                  color="uncolored"
                  size="small"
                  variant="contained"
                  startIcon={<theme.icons.see />}
                  onClick={handleOpen}
                >
                  {t(`See the ${type}`, { ns: 'cta' })}
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    );
  }

  const maxWidth = Device.isMobile && isPortrait ? '90%' : '50%';

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '95dvh' }}>
      {/* Background layer */}
      <Box sx={styles.highlight} />

      {/* Content layer */}
      <Stack
        justifyContent="flex-start"
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 1,
          pointerEvents: 'auto',
        }}
      >
        <Stack mt={10} height="75%" justifyContent="space-between">
          <Extra type={type} text={item.insight} city={item.city} />
          <Insight type={type} item={item} maxWidth={maxWidth} />
        </Stack>
      </Stack>
    </Box>
  );
}

function getRandomSpotItem(categories: ListHomeSpotsQuery['homeSpots'], t: TFunction): Item {
  const category = getRandomItem(categories);
  const spot = getRandomItem(category.spots) as ListHomeSpotsQuery['homeSpots'][0]['spots'][0];
  const cover = getRandomItem(spot?.covers || []);

  // tmp allow spot with no cover for now
  if (!spot?.configuration) {
    return getRandomSpotItem(categories, t);
  }

  const types = [
    spot.configuration.primaryType,
    spot.configuration.secondaryType,
    spot.configuration.tertiaryType,
  ]
    .filter(Boolean)
    .map((type) => t(`types.${type}.label`, { ns: 'spot' }))
    .join(' • ');

  return {
    id: spot.id,
    title: spot.name,
    description: spot.description ?? '',
    city: spot.address?.city ?? '',
    slug: spot.slug,
    cover: cover?.url || `/spot/${spot.configuration.primaryType}.jpg`,
    isGray: spot.covers.length === 0,
    insight: types,
  };
}

function getRandomEventItem(categories: ListHomeEventsQuery['eventCategories']) {
  const category = getRandomItem(categories);
  const event = getRandomItem(category.events);
  const cover = getRandomItem(event?.covers || []);

  if (!cover) {
    return getRandomEventItem(categories);
  }

  return {
    id: event.id,
    title: event.title,
    description: truncateText(event.description, 200),
    city: event.address?.city ?? '',
    insight: moment(event.from).format('dddd MMMM YYYY'),
    cover: cover.url,
  };
}

function getRandomItem(list: any[]) {
  return list[Math.floor(Math.random() * list.length)];
}
